import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import React from 'react'
import {
  AlertBanner,
  Button,
  EmptyState,
  EnumAlertBannerVariant,
  InputRadio,
  Scrollbar,
  SpinnerLoader,
  type IDropdownItem,
  Box,
  Error,
} from 'shared/ui'
import { Select } from 'shared/ui/Select/Select'
import { makeTextFieldIcon, TextField } from 'shared/ui/TextField'
import { getAriaLabel } from 'shared/lib'
import { CountrySelect } from 'shared/ui/CountrySelect/CountrySelect'
import { LINK_HELP_UK_COMPLIANCE } from 'shared/constants/links'
import {
  type IResponseBillingAreaCode,
  type IResponseBillingState,
} from 'entities/Billing/api/types'
import { ITabsEnum } from 'entities/Phone/types'
import { type ChooseNumberTabsStore } from 'entities/Phone/ui/ChooseNumberTabs/store/ChooseNumberTabsStore'
import styles from './styles.module.scss'

type IAvailableTabContentProps = {
  store: ChooseNumberTabsStore
  hideCountrySelect?: boolean
}

export const ChooseNumberTabsContent = observer(
  ({ store, hideCountrySelect }: IAvailableTabContentProps) => {
    const {
      noResults,
      numbersList,
      selectedNumber,
      activeTab,
      state,
      states,
      areaCode,
      areaCodes,
      canSearch,
      statePlaceholder,
      loading,
      loadingNumber,
      countryStore,
      refresh,
      setNumber,
      setState,
      setAreaCode,
      filterByDigitsValue,
      setFilterByDigitsValue,
      searchError,
    } = store

    const handleState = (item: IDropdownItem) => {
      if (loading) return
      setState(item.customFields as IResponseBillingState)
    }

    const handleAreaCode = (item: IDropdownItem) => {
      if (loading) return
      setAreaCode(item.customFields as IResponseBillingAreaCode)
    }

    return (
      <>
        {!hideCountrySelect && <CountrySelect store={countryStore} />}

        {store.showUKComplianceBanner && (
          <AlertBanner
            ariaLabel='UKComplianceRequired'
            variant={EnumAlertBannerVariant.Warning}
            title={
              "Before you can purchase a UK mobile number, you must complete the UK: Regulatory bundle. Once approved, you'll be able to buy and use UK numbers for messaging and calls."
            }
            actionPrimary={{
              text: 'Get approved',
              onAction: store.goToUKCompliance,
            }}
            actionSecondary={{
              text: 'Learn more',
              onAction: () => window.open(LINK_HELP_UK_COMPLIANCE, '_blank', 'noreferrer'),
            }}
          />
        )}

        {(activeTab === ITabsEnum.TOLL_FREE || store.isUKSelected) && (
          <>
            <TextField
              className={classnames(styles.filterByDigitsInput, {
                [styles.error]: searchError,
              })}
              variant={'stroke'}
              size={'medium'}
              value={filterByDigitsValue}
              onChange={setFilterByDigitsValue}
              InputProps={{ placeholder: 'Filter by digits' }}
              error={searchError}
              leftActions={[
                makeTextFieldIcon({
                  icon: 'dialpad',
                  fontSize: 16,
                  color: 'var(--content-primary-tertiary)',
                  margin: '0 0 0 4px',
                }),
              ]}
            />
            {searchError && <Error type={'text'} error={searchError} />}
          </>
        )}

        {activeTab === ITabsEnum.LOCAL && !store.isUKSelected && (
          <>
            <Box marginBottom={1.5}>
              <Select
                fullWidth
                btnProps={{
                  className: styles.textField,
                  fullWidth: true,
                  typeBtn: 'stroke',
                  justifyContent: 'space-between',
                  fontWeight: 400,
                  style: {
                    color: !state ? 'var(--content-primary-disabled)' : undefined,
                  },
                  text: state ? `${state.state_name}, ${state.state_code}` : statePlaceholder,
                  textIcon: 'makerPin',
                }}
                dropdownProps={{
                  margin: -42,
                  textFieldProps: {
                    leftActions: [
                      makeTextFieldIcon({ icon: 'makerPin', className: styles.textFieldIcon }),
                    ],
                    InputProps: {
                      placeholder: 'Search',
                    },
                  },
                  ariaLabel: 'state',
                }}
                textFieldPlaceholderProps={{
                  size: 'medium',
                  variant: 'stroke',
                }}
                text={''}
                value={state?.state_code}
                items={states}
                onSelect={handleState}
              />
            </Box>

            {state?.state_code && (
              <Box marginBottom={3}>
                <Select
                  fullWidth
                  btnProps={{
                    className: styles.textField,
                    fullWidth: true,
                    typeBtn: 'stroke',
                    justifyContent: 'space-between',
                    fontWeight: 400,
                    style: {
                      color: !areaCode ? 'var(--content-primary-disabled)' : undefined,
                    },
                    text: areaCode
                      ? `${areaCode.state_name} - ${areaCode.area_code}`
                      : 'Select an area code (e.g. 561)',
                    textIcon: 'makerPin',
                  }}
                  dropdownProps={{
                    margin: -42,
                    textFieldProps: {
                      leftActions: [
                        makeTextFieldIcon({ icon: 'makerPin', className: styles.textFieldIcon }),
                      ],
                      InputProps: {
                        placeholder: 'Search',
                      },
                    },
                    ariaLabel: 'area',
                  }}
                  text={''}
                  value={areaCode?.area_code}
                  items={areaCodes}
                  onSelect={handleAreaCode}
                />
              </Box>
            )}
          </>
        )}

        <div>
          {(!noResults || loading) && (
            <Button
              fullWidth={false}
              icon={'refresh'}
              text={'Refresh'}
              size={'small'}
              contained={'secondary'}
              onClick={refresh}
              disabled={loading || !canSearch}
            />
          )}
          {(canSearch || loading) && (
            <div
              className={styles.numbersWrap}
              aria-label={getAriaLabel('GetNumberModalContent', 'numbersWrap')}
            >
              {loading && !loadingNumber ? (
                <div
                  className={styles.loader}
                  aria-label={getAriaLabel('GetNumberModalContent', 'loader')}
                >
                  <SpinnerLoader ariaLabel={'ChooseNumberTabsContent'} />
                </div>
              ) : (
                <div
                  className={classnames({
                    [styles.disabledNumbers]: loadingNumber,
                  })}
                >
                  {noResults ? (
                    <div
                      className={styles.noResults}
                      aria-label={getAriaLabel('GetNumberModalContent', 'noResults')}
                    >
                      <EmptyState
                        noIcon
                        typographyProps={{ variant: 'body-sm-regular', ariaLabel: 'EmptyState' }}
                      />
                    </div>
                  ) : (
                    <Scrollbar autoHeightMax={264} autoHeightMin={264} autoHeight={true}>
                      {numbersList.map((number) => (
                        <InputRadio
                          className={styles.radioInput}
                          radioSize={'small'}
                          checked={selectedNumber?.number === number.number}
                          onChecked={() => setNumber(number)}
                          key={number.number}
                          label={number.formatted}
                          rightText={store.textRadioLabel}
                        />
                      ))}
                    </Scrollbar>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    )
  }
)
